import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { companyRequest, CompanyRequestError } from '@/company/api';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import copyCatalog from '@/i18n/ns/app-sharing';
import { useIntlLocale } from '../../intl-locale';
import type {
  AppCompletedVersions,
  AppRestoreCommand,
} from '../../../../src/apps/lifecycle/restore-version.mjs';
import './versions-tab.css';

/** Only completed originals are listed. Restore is the previous code through
 * a new C9 Save; no published pointer, business-data rewind or old consent. */
export function CompletedAppVersions({
  selection,
  session,
  changed,
}: {
  selection: { artifactId: string; versionId: string; revisionId: string };
  session: typeof fetch;
  changed: (saved: AppCompletedVersions['saved']) => void;
}) {
  const copy = useNamespaceCopy(copyCatalog);
  const locale = useIntlLocale();
  const when = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  };
  const [state, setState] = useState<AppCompletedVersions | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [command, setCommand] = useState<AppRestoreCommand | null>(null);
  const path = `/api/v1/company/apps/${encodeURIComponent(selection.artifactId)}`;
  useEffect(() => {
    let alive = true;
    const query = new URLSearchParams({
      versionId: selection.versionId,
      revisionId: selection.revisionId,
    });
    void companyRequest<AppCompletedVersions>(
      `${path}/completed-versions?${query}`,
      'GET',
      undefined,
      session,
    )
      .then((value) => {
        if (alive) setState(value);
      })
      .catch(() => {
        if (alive) setFailure(copy.versionsFailed);
      });
    return () => {
      alive = false;
    };
  }, [path, selection.versionId, selection.revisionId, session, copy.versionsFailed]);
  async function restore() {
    setBusy(true);
    setFailure(null);
    try {
      const original =
        command ??
        (await companyRequest<AppRestoreCommand>(
          `${path}/restore-previous/prepare`,
          'POST',
          selection,
          session,
        ));
      setCommand(original);
      const result = await companyRequest<{ saved: AppCompletedVersions['saved'] }>(
        `${path}/restore-previous`,
        'POST',
        { command: original, confirmed: true },
        session,
      );
      changed(result.saved);
    } catch (error) {
      setFailure(
        error instanceof CompanyRequestError &&
          error.code === 'app_restore_data_or_source_review_required'
          ? copy.restoreIncompatible
          : copy.restoreFailed,
      );
    } finally {
      setBusy(false);
    }
  }
  if (!state) return <p role={failure ? 'alert' : 'status'}>{failure ?? copy.versionsLoading}</p>;
  return (
    <section data-testid="completed-app-versions">
      <ul className="pc-versions-list">
        {state.versions.map((row) => (
          <li key={row.versionId}>
            <span>
              <strong>{formatCopy(copy.version, { n: String(row.number) })}</strong>
              <small>
                {row.actorLabel} · {when(row.createdAt)}
              </small>
            </span>
            {row.current && <span>{copy.current}</span>}
          </li>
        ))}
      </ul>
      {state.canRestore && (
        <>
          <p>{copy.restoreBody}</p>
          <Button
            disabled={busy}
            onClick={() => void restore()}
            data-testid="restore-previous-version"
          >
            {busy ? copy.restoring : copy.restore}
          </Button>
        </>
      )}
      {failure && <p role="alert">{failure}</p>}
    </section>
  );
}
